import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  TextField,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { API_AXIOS } from "../http/interceptor";
import Apis from "../utils/apis";
import { isMediaReference, resolveMediaUrls } from "../utils/media";

const FileUpload = ({
  label,
  name = "files",
  acceptedFormats,
  onChange,
  multiple = false,
  error,
  labelSx,
  existingFiles,
  uploadContext,
  onUploadStatusChange,
  maxFiles = 10,
  disabled = false,
}) => {
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadedNames, setUploadedNames] = useState([]);
  const [resolvedExistingFiles, setResolvedExistingFiles] = useState({});

  const currentFiles = useMemo(
    () =>
      (Array.isArray(existingFiles) ? existingFiles : [existingFiles]).filter(
        (file) => typeof file === "string" && file.trim(),
      ),
    [existingFiles],
  );

  useEffect(() => {
    let active = true;

    if (currentFiles.length === 0) {
      setResolvedExistingFiles({});
      return () => {
        active = false;
      };
    }

    resolveMediaUrls(currentFiles)
      .then((urls) => {
        if (active) setResolvedExistingFiles(urls);
      })
      .catch(() => {
        if (active) setResolvedExistingFiles({});
      });

    return () => {
      active = false;
    };
  }, [currentFiles]);

  const getFileLabel = (reference, index) => {
    if (isMediaReference(reference)) return `Existing file ${index + 1}`;

    try {
      const fileName = decodeURIComponent(
        new URL(reference).pathname.split("/").filter(Boolean).pop() || "",
      );
      return fileName || `Existing file ${index + 1}`;
    } catch {
      return `Existing file ${index + 1}`;
    }
  };

  const handleFileChange = async (e) => {
    // Multiple
    const selectedFile = Array.from(e.target.files);

    if (selectedFile.length) {
      const totalSize = selectedFile.reduce((sum, item) => sum + item.size, 0);
      if (selectedFile.length > maxFiles || totalSize > 100 * 1024 * 1024 || selectedFile.some(file => file.size > 25 * 1024 * 1024)) {
        setFileError(`Select at most ${maxFiles} files, up to 25 MB each and 100 MB combined.`);
        onUploadStatusChange?.({ loading: false, error: true });
        e.target.value = "";
        return;
      }
      setFileError("");

      try {
        setLoading(true);
        onUploadStatusChange?.({ loading: true, error: false });
        const requestConfig = uploadContext ? {
          headers: uploadContext.token ? { "x-invitation-token": uploadContext.token } : {},
          timeout: 30000,
        } : undefined;
        const context = uploadContext ? {
          onboardingUserId: uploadContext.userId,
          onboardingField: name,
        } : {};
        const uploadedFiles = await Promise.all(
          selectedFile.map(async (selected) => {
            const extension = selected.name.split(".").pop()?.toLowerCase();
            const inferredTypes = {
              jpg: "image/jpeg",
              jpeg: "image/jpeg",
              png: "image/png",
              gif: "image/gif",
              webp: "image/webp",
              heic: "image/heic",
              heif: "image/heif",
              pdf: "application/pdf",
              csv: "text/csv",
              txt: "text/plain",
              doc: "application/msword",
              docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              xls: "application/vnd.ms-excel",
              xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              mp4: "video/mp4",
              mov: "video/quicktime",
            };
            const contentType = selected.type || inferredTypes[extension];
            const { data: presigned } = await API_AXIOS.post(Apis.mediaPresign, {
              fileName: selected.name,
              contentType,
              size: selected.size,
              category: name,
              ...context,
            }, requestConfig);

            const s3Form = new FormData();
            Object.entries(presigned.upload.fields).forEach(([key, value]) => {
              s3Form.append(key, value);
            });
            s3Form.append("file", selected);
            await axios.post(presigned.upload.url, s3Form, { timeout: 120000 });

            const { data: completed } = await API_AXIOS.post(
              Apis.mediaComplete(presigned.assetId),
              context,
              requestConfig,
            );
            return completed;
          }),
        );

        onChange({
          message: "Files uploaded successfully",
          urls: uploadedFiles.map((item) => item.reference),
          files: uploadedFiles,
        });
        setUploadedNames(uploadedFiles.map(item => item.name));
        onUploadStatusChange?.({ loading: false, error: false });
      } catch (error) {
        console.error("File upload failed", error);
        setFileError(
          error.response?.data?.error || error.response?.data?.message || error.message || "Upload failed. Please try again."
        );
        onUploadStatusChange?.({ loading: false, error: true });
      } finally {
        setLoading(false);
        e.target.value = "";
      }
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          fontSize: 18,
          fontWeight: 500,
          mb: 1,
          ...labelSx,
        }}
      >
        {label}
      </Box>
      <TextField
        type="file"
        name={name}
        accept={acceptedFormats}
        fullWidth
        size="small"
        onChange={handleFileChange}
        disabled={loading || disabled}
        InputProps={{
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        inputProps={{
          multiple,
          accept: acceptedFormats,
        }}
      />

      {currentFiles.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ color: "text.secondary", fontSize: 13, mb: 0.75 }}>
            Currently attached ({currentFiles.length})
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {currentFiles.map((reference, index) => {
              const url = resolvedExistingFiles[reference];
              return (
                <Button
                  key={`${reference}-${index}`}
                  component={url ? "a" : "button"}
                  href={url || undefined}
                  target={url ? "_blank" : undefined}
                  rel={url ? "noopener noreferrer" : undefined}
                  size="small"
                  variant="outlined"
                  disabled={!url}
                  sx={{
                    maxWidth: "100%",
                    textTransform: "none",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box
                    component="span"
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {getFileLabel(reference, index)}
                  </Box>
                </Button>
              );
            })}
          </Box>
        </Box>
      )}

      {uploadedNames.length > 0 && <Box sx={{ color: "success.main", mt: 1 }}>Uploaded: {uploadedNames.join(", ")}</Box>}
      {fileError && <Box sx={{ color: "red", mt: 1 }}>{fileError}</Box>}
      {fileError && uploadContext && !loading && (
        <Button type="button" size="small" onClick={() => {
          setFileError("");
          setUploadedNames([]);
          onChange({ urls: [], files: [] });
          onUploadStatusChange?.({ loading: false, error: false });
        }}>Clear selection</Button>
      )}
      {error && <Box sx={{ color: "red", mt: 1 }}>{error}</Box>}
    </Box>
  );
};

export default FileUpload;
